/**
 * FieldParityBadge — display-only RU/KY/EN presence chips for the Field
 * Library table (PIM Wave P1 Task 3, design notes Surface 1 "i18n parity
 * badge"). Mirrors `TrilingualInput`'s parity-chip visual language
 * (`_shared/TrilingualInput.tsx`) but is a tiny, read-only, non-interactive
 * sibling — the table cell has no active-language concept to switch, and
 * `TrilingualInput` itself is an authoring control (Segmented switch, RHF
 * wiring) that would be the wrong shape here. RU is always "filled" for a
 * saved field (name_ru is required server-side); KY/EN render "warn" when
 * absent, matching the same amber "missing translation" tone as the
 * authoring control.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";

export interface FieldParityBadgeProps {
  nameRu: string;
  nameKy: string | null;
  nameEn: string | null;
}

const LANGS = ["ru", "ky", "en"] as const;
type Lang = (typeof LANGS)[number];
const LANG_CODE: Record<Lang, string> = { ru: "RU", ky: "KG", en: "EN" };

const TONE_CLASS: Record<"filled" | "warn", string> = {
  filled: "border-transparent bg-primary text-primary-foreground",
  warn: "border-transparent bg-[var(--g-warn)]/10 text-[var(--g-warn)]",
};

export function FieldParityBadge({
  nameRu,
  nameKy,
  nameEn,
}: FieldParityBadgeProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const values: Record<Lang, string | null> = { ru: nameRu, ky: nameKy, en: nameEn };

  return (
    <div
      className="flex items-center gap-1"
      role="group"
      aria-label={t("pim.fields.columns.parity")}
      data-testid="pim-fields-parity-badge"
    >
      {LANGS.map((lang) => {
        const hasValue = Boolean(values[lang]?.trim());
        const tone = hasValue ? "filled" : "warn";
        const code = LANG_CODE[lang];
        return (
          <span
            key={lang}
            data-testid="pim-fields-parity-chip"
            data-lang={lang}
            data-tone={tone}
            title={
              hasValue
                ? undefined
                : t("pim.trilingual.missingTranslationHint", { code })
            }
            className={[
              "inline-flex h-5 min-w-5 items-center justify-center rounded-full border px-1.5 text-[10px] font-semibold uppercase tracking-wide",
              TONE_CLASS[tone],
            ].join(" ")}
          >
            {code}
          </span>
        );
      })}
    </div>
  );
}

export default FieldParityBadge;
